import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeaderTabs } from './PageHeaderTabs';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { useI18n } from '../../../i18n';

type DisciplineKey = 'structure' | 'process' | 'information' | 'compliance' | 'tech' | 'reputation';

interface TabPanel {
  label: string;
  component: React.ReactNode;
  key?: string;
}

interface DisciplinePageProps {
  tabs: TabPanel[];
  accentColor?: string;
  defaultTab?: number;
  /**
   * Identificador de la ingeniería para mostrar un InfoTooltip contextual
   * con la descripción de la disciplina en el header. Si se omite, no se
   * muestra tooltip (retrocompatible).
   */
  disciplineKey?: DisciplineKey;
  /**
   * Título mostrado en el tooltip. Si se omite, se usa el de la disciplina.
   */
  disciplineTitle?: string;
}

function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function DisciplinePage({
  tabs,
  accentColor,
  defaultTab = 0,
  disciplineKey,
  disciplineTitle,
}: DisciplinePageProps) {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const initialTab = tabParam
    ? tabs.findIndex((tab) => (tab.key || slugify(tab.label)) === tabParam)
    : defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : defaultTab);

  // 0618 fix: sincronizar activeTab con el query param tab cuando cambia desde
  // navegacion externa (ej: PuestoViewerModal.onVerOrganigrama -> navigate(?tab=organigrama)).
  // Sin esto, useState solo lee el param en el mount inicial y los cambios subsiguientes
  // del URL no actualizan la pestana activa.
  useEffect(() => {
    if (!tabParam) return;
    const idx = tabs.findIndex((tab) => (tab.key || slugify(tab.label)) === tabParam);
    if (idx >= 0 && idx !== activeTab) {
      setActiveTab(idx);
    }
    // tabs cambia por referencia en cada render -> excluido para evitar loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  function handleTabChange(index: number) {
    setActiveTab(index);
    const slug = tabs[index].key || slugify(tabs[index].label);
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, tab: slug }, { replace: true });
  }

  const tooltipText = disciplineKey ? t.disciplines.tooltips[disciplineKey] : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ position: 'relative' }}>
        <PageHeaderTabs
          tabs={tabs.map((tab) => tab.label)}
          active={activeTab}
          onChange={handleTabChange}
          accentColor={accentColor}
        />
        {tooltipText && (
          <div
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'inline-flex',
            }}
          >
            <InfoTooltip title={disciplineTitle} text={tooltipText} position="left" />
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px', backgroundColor: '#F8F8F8' }}>
        {tabs[activeTab]?.component}
      </div>
    </div>
  );
}
